import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Grid,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { fetchTrendRunPreviewResults } from '../service';
import { extractTrendUiErrorMessage } from '../errorUtils';
import type {
  TrendPipelineRun,
  TrendPreviewItemResult,
  TrendPreviewItemStatus,
  TrendPreviewRunArtifact,
  TrendTestSetSummary,
} from '../types';
import { DISPLAY_FONT, TREND_COLORS } from '../trendTheme';

type PreviewFilter = 'all' | TrendPreviewItemStatus;
const PREVIEW_RESULT_RUN_MODES = new Set([
  'micro_test',
  'source_test',
  'category_preview',
  'brand_radar',
  'prompt_test',
  'model_test',
]);

const FILTER_LABELS: Record<PreviewFilter, string> = {
  all: 'Alla källposter',
  publishable: 'Publicerbara',
  blocked: 'Blockerade',
  ignored: 'Ignorerade',
  requires_review: 'Kräver granskning',
};

function getRunProfileLabel(profile: TrendPipelineRun['profile']): string {
  if (profile === 'micro') return 'Mikrotest';
  if (profile === 'mini') return 'Minikörning';
  return 'Full körning';
}

function getRunInputModeLabel(inputMode: TrendPipelineRun['inputMode']): string {
  if (inputMode === 'fixture') return 'Testdata';
  if (inputMode === 'source_items') return 'Valda källposter';
  return 'Livekällor';
}

function getPreviewStatusLabel(status: TrendPreviewItemStatus): string {
  if (status === 'publishable') return 'Publicerbar';
  if (status === 'requires_review') return 'Kräver granskning';
  if (status === 'ignored') return 'Ignorerad';
  return 'Blockerad';
}

function getPreviewStatusColor(status: TrendPreviewItemStatus): 'success' | 'warning' | 'default' | 'info' {
  if (status === 'publishable') return 'success';
  if (status === 'requires_review') return 'info';
  if (status === 'blocked') return 'warning';
  return 'default';
}

function formatShortDate(value?: string | null): string {
  if (!value) return 'Utan datum';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Utan datum';
  return date.toISOString().slice(0, 10);
}

function formatDateTime(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 19).replace('T', ' ');
}

function buildRunOptionLabel(run: TrendPipelineRun): string {
  const startedAt = formatDateTime(run.startedAt) || 'Utan starttid';
  const fixtureLabel = run.fixtureKey ? ` · ${run.fixtureKey}` : '';
  return `${startedAt} · ${getRunProfileLabel(run.profile)}${fixtureLabel}`;
}

function getDisplayTitle(item: TrendPreviewItemResult): string {
  return item.article?.title || item.title || 'Testpost';
}

function getStatusAlertSeverity(status: TrendPreviewItemStatus): 'info' | 'warning' {
  return status === 'ignored' ? 'info' : 'warning';
}

function getSourceContentStatusLabel(status: TrendPreviewItemResult['textQuality']['contentStatus']): string {
  if (status === 'full_article_text') return 'Full artikeltext';
  if (status === 'teaser_snippet') return 'Teaser/snippet';
  if (status === 'boilerplate_dominated') return 'Boilerplate';
  if (status === 'requires_review') return 'Kräver granskning';
  return 'Okänd underlagstyp';
}

function getSourceReviewStatusLabel(status: TrendPreviewItemResult['textQuality']['reviewStatus']): string {
  if (status === 'required') return 'Granskning krävs';
  if (status === 'completed') return 'Granskning klar';
  return 'Ingen granskning krävs';
}

type Props = {
  runs: TrendPipelineRun[];
  testSets: TrendTestSetSummary[];
};

function getAnalysisLabel(item: TrendPreviewItemResult): string {
  if (item.analysis.usedFallback && !item.analysis.aiAvailable) return 'Reservlogik utan AI';
  if (item.analysis.usedFallback) return 'Reservlogik';
  return 'AI-utkast';
}

function getFixtureUsageCopy(testSet: TrendTestSetSummary | null): {
  headline: string;
  body: string;
} | null {
  if (!testSet) return null;

  if (testSet.key === 'micro-smoke-v1') {
    return {
      headline: 'Så använder du det här testet',
      body: 'Det här är en snabb hälsokontroll. Här vill du normalt se att minst några källposter blir publicerbara. Om detta test plötsligt blir rött har något grundläggande gått sönder i testkedjan.',
    };
  }
  if (testSet.key === 'quality-regression-v1') {
    return {
      headline: 'Så använder du det här testet',
      body: 'Det här är medvetet ett problempaket. Här är det normalt att många källposter ignoreras eller stoppas. Nyttan är att verifiera att kvalitetskontraktet faktiskt fångar tunt, gammalt eller brusigt underlag.',
    };
  }
  if (testSet.key === 'image-regression-v1') {
    return {
      headline: 'Så använder du det här testet',
      body: 'Det här testet fokuserar främst på bildlogik. Här är det viktigare att testet hittar huvudbilds- eller galleriunderlag än att alla källposter blir publicerbara artiklar.',
    };
  }
  if (testSet.key === 'structure-regression-v1') {
    return {
      headline: 'Så använder du det här testet',
      body: 'Det här paketet verifierar Fas 2-strukturen. Här ska du kontrollera att testet visar tydlig artikelintro, viktiga punkter, källfakta och visuella datapunkter i stället för ett platt textblock.',
    };
  }
  if (testSet.key === 'readability-fallback-v1') {
    return {
      headline: 'Så använder du det här testet',
      body: 'Det här paketet pressar extraktionen med stökig HTML och reservspår. Här ska du främst validera att huvudtexten överlever och att testet fortfarande bygger ett begripligt artikelutkast.',
    };
  }

  return {
    headline: 'Så använder du det här testet',
    body: testSet.description || 'Det här testdatapaketet används för att validera en specifik del av testkedjan innan du kör en lång livekörning.',
  };
}

export default function TrendPreviewRunResultsPanel({ runs, testSets }: Props) {
  const previewRuns = useMemo(
    () => runs.filter((run) => (
      run.scope === 'preview'
      && (!run.runMode || PREVIEW_RESULT_RUN_MODES.has(run.runMode))
    )),
    [runs],
  );
  const [selectedRunId, setSelectedRunId] = useState('');
  const [previewFilter, setPreviewFilter] = useState<PreviewFilter>('all');
  const [artifact, setArtifact] = useState<TrendPreviewRunArtifact | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (previewRuns.length === 0) {
      setSelectedRunId('');
      return;
    }

    const selectedExists = previewRuns.some((run) => run._id === selectedRunId);
    if (selectedExists) return;

    const preferredRun = previewRuns.find((run) => run.status === 'completed') || previewRuns[0];
    setSelectedRunId(preferredRun._id);
  }, [previewRuns, selectedRunId]);

  const selectedRun = useMemo(
    () => previewRuns.find((run) => run._id === selectedRunId) || null,
    [previewRuns, selectedRunId],
  );
  const selectedTestSet = useMemo(
    () => testSets.find((entry) => entry.key === selectedRun?.fixtureKey) || null,
    [selectedRun?.fixtureKey, testSets],
  );
  const fixtureUsageCopy = useMemo(
    () => getFixtureUsageCopy(selectedTestSet),
    [selectedTestSet],
  );

  useEffect(() => {
    if (!selectedRun) {
      setArtifact(null);
      setError(null);
      return;
    }
    if (selectedRun.status !== 'completed') {
      setArtifact(null);
      setError(null);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);
    void fetchTrendRunPreviewResults(selectedRun._id)
      .then((result) => {
        if (!active) return;
        setArtifact(result);
      })
      .catch((err: any) => {
        if (!active) return;
        setArtifact(null);
        setError(extractTrendUiErrorMessage(err, 'Kunde inte ladda testresultaten för körningen.'));
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedRun]);

  const filteredItems = useMemo(() => {
    if (!artifact) return [];
    if (previewFilter === 'all') return artifact.items;
    return artifact.items.filter((item) => item.status === previewFilter);
  }, [artifact, previewFilter]);
  const derivedCounts = useMemo(() => {
    if (!artifact) {
      return {
        testedItems: 0,
        analyzedItems: 0,
        publishableItems: 0,
        blockedItems: 0,
        ignoredItems: 0,
        reviewItems: 0,
        heroImageHits: 0,
        fallbackItems: 0,
      };
    }
    return {
      testedItems: artifact.items.length,
      analyzedItems: artifact.items.filter((entry) => entry.article).length,
      publishableItems: artifact.items.filter((entry) => entry.status === 'publishable').length,
      blockedItems: artifact.items.filter((entry) => entry.status === 'blocked').length,
      ignoredItems: artifact.items.filter((entry) => entry.status === 'ignored').length,
      reviewItems: artifact.items.filter((entry) => entry.status === 'requires_review').length,
      heroImageHits: artifact.items.filter((entry) => !!entry.article?.heroImage).length,
      fallbackItems: artifact.items.filter((entry) => entry.analysis.usedFallback).length,
    };
  }, [artifact]);

  const runStateMessage = (() => {
    if (!selectedRun) return '';
    if (selectedRun.status === 'completed') return '';
    if (selectedRun.status === 'failed') {
        return selectedRun.error || 'Testkörningen misslyckades innan resultat kunde sparas.';
    }
    if (selectedRun.status === 'cancelled' || selectedRun.status === 'cancelling') {
      return 'Testkörningen avbröts innan resultat kunde visas.';
    }
    return 'Testresultat blir tillgängliga när körningen är klar.';
  })();

  return (
    <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        spacing={2}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: '0.22em',
              color: TREND_COLORS.textMuted,
              fontWeight: 700,
              fontFamily: DISPLAY_FONT,
            }}
          >
            TESTRESULTAT
          </Typography>
          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mt: 0.75, maxWidth: 780 }}>
            Här ser du vad varje testkörning faktiskt producerade: vilka källposter som blev publicerbara,
            vilka som blockerades eller ignorerades, och varför.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', lg: 'auto' } }}>
          <TextField
            select
            size="small"
            label="Körning"
            value={selectedRunId}
            onChange={(event) => setSelectedRunId(event.target.value)}
            sx={{ minWidth: { xs: '100%', md: 360 } }}
            disabled={previewRuns.length === 0}
          >
            {previewRuns.length === 0 ? (
              <MenuItem value="">Inga testkörningar ännu</MenuItem>
            ) : (
              previewRuns.map((run) => (
                <MenuItem key={run._id} value={run._id}>
                  {buildRunOptionLabel(run)}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            size="small"
            label="Filter"
            value={previewFilter}
            onChange={(event) => setPreviewFilter(event.target.value as PreviewFilter)}
            sx={{ minWidth: { xs: '100%', md: 190 } }}
            disabled={!artifact}
          >
            <MenuItem value="all">{FILTER_LABELS.all}</MenuItem>
            <MenuItem value="publishable">{FILTER_LABELS.publishable}</MenuItem>
            <MenuItem value="blocked">{FILTER_LABELS.blocked}</MenuItem>
            <MenuItem value="ignored">{FILTER_LABELS.ignored}</MenuItem>
            <MenuItem value="requires_review">{FILTER_LABELS.requires_review}</MenuItem>
          </TextField>
        </Stack>
      </Stack>

      {previewRuns.length === 0 && (
        <Alert severity="info" sx={{ mt: 2.5 }}>
          Inga testkörningar har sparats ännu. Kör först ett mikrotest eller en minikörning i testläge.
        </Alert>
      )}

      {selectedRun && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2.5 }}>
          <Chip size="small" label={getRunProfileLabel(selectedRun.profile)} />
          <Chip size="small" label={getRunInputModeLabel(selectedRun.inputMode)} />
          <Chip size="small" label={selectedRun.status} />
          {selectedRun.fixtureKey && <Chip size="small" label={`Testdata: ${selectedRun.fixtureKey}`} />}
          {selectedRun.startedAt && <Chip size="small" variant="outlined" label={formatDateTime(selectedRun.startedAt)} />}
        </Stack>
      )}

      {loading && <LinearProgress sx={{ mt: 2.5, borderRadius: 1 }} />}
      {!loading && error && <Alert severity="error" sx={{ mt: 2.5 }}>{error}</Alert>}
      {!loading && !error && runStateMessage && (
        <Alert severity={selectedRun?.status === 'failed' ? 'error' : 'info'} sx={{ mt: 2.5 }}>
          {runStateMessage}
        </Alert>
      )}

      {!loading && !error && artifact && (
        <Stack spacing={2.5} sx={{ mt: 2.5 }}>
          <Alert severity="info">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Så läser du testet
            </Typography>
            <Typography variant="body2">
              `Publicerbar` betyder att ett testutkast byggdes och klarade kvalitetskontraktet.
              `Kräver granskning` betyder att underlaget eller artikelkärnan ser lovande ut men inte är trygg nog för automatisk vidareanvändning.
              `Blockerad` betyder att ett utkast byggdes men stoppades efter analys.
              `Ignorerad` betyder att itemet stoppades innan artikelutkast skapades, oftast för att underlaget är för tunt eller för gammalt.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              Testdata är inte färdiga slutartiklar. Därför kan testet visa engelska källtitlar, exempelbilder eller reservlogik.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              I Fas 2 ska testet också visa den strukturerade artikelmodellen, så att du kan verifiera artikelintro, källfakta och visuella datapunkter innan du kör skarpt pipelineflöde.
            </Typography>
          </Alert>

          {fixtureUsageCopy && (
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {fixtureUsageCopy.headline}
              </Typography>
              <Typography variant="body2">{fixtureUsageCopy.body}</Typography>
              {selectedTestSet?.description && (
                <Typography variant="body2" sx={{ mt: 0.75, color: TREND_COLORS.textSecondary }}>
                  Testdatabeskrivning: {selectedTestSet.description}
                </Typography>
              )}
            </Alert>
          )}

          <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
              {artifact.summary.headline || 'Testsammanfattning'}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              <Chip size="small" label={`Testade ${derivedCounts.testedItems}`} />
              <Chip size="small" color="success" label={`Publicerbara ${derivedCounts.publishableItems}`} />
              <Chip size="small" color="info" label={`Kräver granskning ${derivedCounts.reviewItems}`} />
              <Chip size="small" color="warning" label={`Blockerade ${derivedCounts.blockedItems}`} />
              <Chip size="small" label={`Ignorerade ${derivedCounts.ignoredItems}`} />
              <Chip size="small" label={`Artikelutkast ${derivedCounts.analyzedItems}`} />
              <Chip size="small" label={`Huvudbildsträff ${derivedCounts.heroImageHits}`} />
              <Chip size="small" label={`Reservlogik ${derivedCounts.fallbackItems}`} />
            </Stack>
            <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: TREND_COLORS.textMuted }}>
              Genererad: {formatDateTime(artifact.generatedAt) || 'okänt'}
            </Typography>
            {artifact.notes.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                {artifact.notes.slice(0, 6).map((note) => (
                  <Typography key={note} variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                    {note}
                  </Typography>
                ))}
              </Stack>
            )}
          </Box>

          {artifact.topBlockingReasons.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ display: 'block', color: TREND_COLORS.textMuted, mb: 1 }}>
                Vanligaste orsaker till att källposter inte gick vidare
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {artifact.topBlockingReasons.map((entry) => (
                  <Chip
                    key={`${entry.reason}-${entry.count}`}
                    size="small"
                    variant="outlined"
                    label={`${entry.reason} (${entry.count})`}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary }}>
            Visar {filteredItems.length} av {artifact.items.length} källposter för filtret "{FILTER_LABELS[previewFilter].toLowerCase()}".
          </Typography>

          {filteredItems.length === 0 ? (
            <Alert severity="info">
              Inga testposter matchar det valda filtret för den här körningen.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {filteredItems.map((item) => {
                const displayTitle = getDisplayTitle(item);
                const showOriginalTitle = displayTitle && item.title && displayTitle !== item.title;
                const hasImage = !!item.article?.heroImage;
                const structuredSections = item.article
                  ? [
                    {
                      key: 'keyPoints',
                      label: 'Viktigaste punkter',
                      entries: item.article.keyPoints,
                      warning: false,
                    },
                    {
                      key: 'sourceFacts',
                      label: 'Källfakta',
                      entries: item.article.sourceFacts,
                      warning: false,
                    },
                    {
                      key: 'sourceWarnings',
                      label: 'Osäkerheter',
                      entries: item.article.sourceWarnings,
                      warning: true,
                    },
                    {
                      key: 'visualInsights',
                      label: 'Visuella datapunkter',
                      entries: item.article.visualInsights,
                      warning: false,
                    },
                  ].filter((section) => section.entries.length > 0)
                  : [];

                return (
                  <Grid key={`${artifact.runId}-${item.inputKey}`} size={{ xs: 12, xl: 6 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: TREND_COLORS.sectionBg,
                        border: `1px solid ${TREND_COLORS.divider}`,
                        height: '100%',
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Box
                          sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            height: 180,
                            bgcolor: TREND_COLORS.softNavy,
                            border: `1px solid ${TREND_COLORS.divider}`,
                            backgroundImage: hasImage ? `url(${item.article?.heroImage})` : 'none',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {!hasImage && (
                            <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                              Ingen huvudbild i testet
                            </Typography>
                          )}
                        </Box>

                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          spacing={1}
                          alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                              {displayTitle}
                            </Typography>
                            {showOriginalTitle && (
                              <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block', mt: 0.35 }}>
                                Originalrubrik: {item.title}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            size="small"
                            color={getPreviewStatusColor(item.status)}
                            label={getPreviewStatusLabel(item.status)}
                          />
                        </Stack>

                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {item.category && <Chip size="small" variant="outlined" label={item.category} />}
                          {item.sourceName && <Chip size="small" variant="outlined" label={item.sourceName} />}
                          <Chip size="small" variant="outlined" label={formatShortDate(item.publishedAt)} />
                          <Chip size="small" variant="outlined" label={getAnalysisLabel(item)} />
                          <Chip size="small" variant="outlined" label={getSourceContentStatusLabel(item.textQuality.contentStatus)} />
                          {item.article && <Chip size="small" variant="outlined" label={`Galleri ${item.article.galleryImageCount}`} />}
                        </Stack>

                        {item.article?.excerpt && (
                          <Typography variant="body2" sx={{ color: TREND_COLORS.navy, fontWeight: 700, lineHeight: 1.6 }}>
                            {item.article.excerpt}
                          </Typography>
                        )}

                        {item.article?.bodyPreview ? (
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.8 }}>
                            {item.article.bodyPreview}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, fontStyle: 'italic' }}>
                            {item.status === 'ignored'
                              ? 'Ingen artikeltext genererades eftersom itemet ignorerades före analys.'
                              : item.status === 'requires_review'
                                ? 'Artikelkärnan stoppades och kräver manuell granskning innan vidare aktivering.'
                              : 'Ingen artikeltext genererades för detta item.'}
                          </Typography>
                        )}

                        {structuredSections.length > 0 && (
                          <Grid container spacing={1.5}>
                            {structuredSections.map((section) => (
                              <Grid key={section.key} size={{ xs: 12, md: 6 }}>
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: section.warning ? '#FFF4D6' : TREND_COLORS.cardBg,
                                    border: section.warning ? '1px solid #F0D18A' : `1px solid ${TREND_COLORS.divider}`,
                                    height: '100%',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      color: section.warning ? '#8A5D00' : TREND_COLORS.textMuted,
                                      fontWeight: 700,
                                      letterSpacing: '0.08em',
                                      textTransform: 'uppercase',
                                      mb: 0.9,
                                    }}
                                  >
                                    {section.label}
                                  </Typography>
                                  <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                                    {section.entries.slice(0, 3).map((entry) => (
                                      <Typography
                                        component="li"
                                        key={`${section.key}-${entry}`}
                                        variant="body2"
                                        sx={{ color: section.warning ? '#6E4C00' : TREND_COLORS.textSecondary, lineHeight: 1.65 }}
                                      >
                                        {entry}
                                      </Typography>
                                    ))}
                                  </Stack>
                                  {section.entries.length > 3 && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: TREND_COLORS.textMuted }}>
                                      +{section.entries.length - 3} till i artikelutkastet
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        )}

                        {item.reasons.length > 0 && (
                          <Alert severity={getStatusAlertSeverity(item.status)}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                              {item.status === 'ignored'
                                ? 'Varför itemet ignorerades före analys'
                                : item.status === 'requires_review'
                                  ? 'Varför källposten kräver granskning'
                                  : 'Varför itemet blockerades efter analys'}
                            </Typography>
                            <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
                              {item.reasons.map((reason) => (
                                <Typography component="li" key={reason} variant="body2">
                                  {reason}
                                </Typography>
                              ))}
                            </Stack>
                          </Alert>
                        )}

                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: TREND_COLORS.cardBg, border: `1px dashed ${TREND_COLORS.divider}` }}>
                          <Typography variant="caption" sx={{ display: 'block', color: TREND_COLORS.textMuted }}>
                            Textkvalitet: {item.textQuality.contentLength} tecken · {item.textQuality.longSentenceCount} längre meningar · {item.textQuality.uniqueWordCount} unika ord
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: TREND_COLORS.textMuted, mt: 0.35 }}>
                            Underlag: {getSourceContentStatusLabel(item.textQuality.contentStatus)} · {getSourceReviewStatusLabel(item.textQuality.reviewStatus)}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: TREND_COLORS.textMuted, mt: 0.35 }}>
                            Färskhet: {item.freshness.reason}
                          </Typography>
                        </Box>

                        {item.url && (
                          <Link
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            variant="body2"
                            sx={{ overflowWrap: 'anywhere' }}
                          >
                            Öppna originalkälla
                          </Link>
                        )}
                      </Stack>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Stack>
      )}
    </Paper>
  );
}
